import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JsonEditorComponent, JsonEditorMode, JsonEditorOptions } from 'ang-jsoneditor';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { ConfirmationService } from 'primeng/api';

export interface ConfigFileOption {
  label: string;
  value: string;
}

/**
 * Shared presentational config editor used by both the device dashboard
 * (pages/settings/config) and the cloud portal (cloud/pages/device/config).
 * Hosts own all API calls; this component owns the editor UI: mode switching
 * (view/tree/code), dirty tracking, search, download, and the new/delete
 * dialogs. Push new data via the `config`/`schema` inputs — a fresh object
 * reference is required to trigger a reload of the editor contents.
 */
@Component({
  selector: 'app-config-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    JsonEditorComponent,
    ButtonModule,
    SelectModule,
    DialogModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    TooltipModule,
    ConfirmDialogModule,
    MessageModule
  ],
  providers: [ConfirmationService],
  templateUrl: './config-editor.component.html',
  styleUrl: './config-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfigEditorComponent {
  // --- Data ---
  config = input<unknown | null>(null);
  schema = input<unknown | null>(null);

  // --- File selection ---
  files = input<ConfigFileOption[]>([]);
  selectedFile = input<string>('');
  fileSelected = output<string>();

  // --- Behavior flags ---
  /** Disables every mutating action (edit, save, new, delete), e.g. when the device is offline. */
  readonly = input(false);
  /** Whether the currently loaded file may be edited. Creating a new file stays available (e.g. default files). */
  canEdit = input(true);
  canDelete = input(true);
  showTemplateButton = input(true);
  templateDisabled = input(false);
  loading = input(false);
  saving = input(false);
  errorMessage = input<string | null>(null);
  newFileSuffix = input<string | null>(null);
  downloadFileName = input<string>('config.json');

  // --- Status bar ---
  lastSaved = input<string | Date | null>(null);
  isDefault = input(false);
  restartRequired = input(false);
  configPath = input<string | null>(null);

  // --- Events ---
  save = output<unknown>();
  reloadRequested = output<void>();
  createFile = output<string>();
  deleteFile = output<void>();
  templateClick = output<void>();
  dirtyChange = output<boolean>();

  // --- Internal state ---
  private jsonEditor = viewChild<JsonEditorComponent>('jsonEditor');
  private host = inject(ElementRef<HTMLElement>);
  private confirmationService = inject(ConfirmationService);

  editable = computed(() => !this.readonly() && this.canEdit());

  editorOptions = new JsonEditorOptions();
  mode = signal<JsonEditorMode>('view');
  canSave = signal(false);
  jsonValid = signal(true);
  newDialogVisible = signal(false);
  newFileName = signal('');

  private savedConfigString = '';
  private currentSearch: string | null = null;
  private searchIndex = 0;

  constructor() {
    this.editorOptions.mode = 'view';
    this.editorOptions.statusBar = false;
    this.editorOptions.navigationBar = true;
    this.editorOptions.mainMenuBar = false;
    this.editorOptions.onChangeText = (json: string) => {
      if (!this.editable()) return;
      const valid = this.jsonEditor()?.isValidJson() ?? false;
      this.jsonValid.set(valid);
      const dirty = valid && json !== this.savedConfigString;
      this.canSave.set(dirty);
      this.dirtyChange.emit(dirty);
    };

    // Push new data into the editor whenever the host loads a config.
    effect(() => {
      const editor = this.jsonEditor();
      const config = this.config();
      const schema = this.schema();
      if (!editor || config === null || config === undefined) return;
      untracked(() => {
        editor.set(config as never);
        if (schema) {
          editor.setSchema(schema, null);
        }
        this.savedConfigString = JSON.stringify(config);
        this.canSave.set(false);
        this.jsonValid.set(true);
        this.dirtyChange.emit(false);
        this.setMode('view');
      });
    });

    // Going non-editable (e.g. cloud tunnel drops, default file loaded) forces the editor back to view mode.
    effect(() => {
      if (!this.editable() && untracked(() => this.mode()) !== 'view') {
        untracked(() => this.setMode('view'));
      }
    });
  }

  setMode(mode: JsonEditorMode): void {
    const editor = this.jsonEditor();
    if (!editor) return;
    editor.setMode(mode);
    this.mode.set(mode);
    // Mode switches rebuild the editor DOM, dropping search highlights — restart the search cycle
    this.currentSearch = null;
    this.searchIndex = 0;
  }

  expandAll(): void {
    this.jsonEditor()?.expandAll();
  }

  collapseAll(): void {
    this.jsonEditor()?.collapseAll();
  }

  requestSave(): void {
    const editor = this.jsonEditor();
    if (!editor || !this.editable() || !this.canSave()) return;
    this.save.emit(editor.get());
  }

  revert(): void {
    const editor = this.jsonEditor();
    if (!editor || !this.savedConfigString) return;
    editor.set(JSON.parse(this.savedConfigString));
    this.canSave.set(false);
    this.jsonValid.set(true);
    this.dirtyChange.emit(false);
  }

  onSearchEnter(value: string): void {
    const editor = this.jsonEditor();
    if (!editor) return;
    if (this.currentSearch !== value) {
      this.currentSearch = value;
      this.searchIndex = 0;
      editor.expandAll();
      editor.search(value);
    } else {
      this.searchIndex++;
    }
    const elements = this.host.nativeElement.querySelectorAll('.jsoneditor-highlight');
    if (elements.length === 0) return;
    if (this.searchIndex >= elements.length) {
      this.searchIndex = 0;
    }
    elements[this.searchIndex].scrollIntoView(true);
  }

  download(): void {
    const config = this.config();
    if (config === null || config === undefined) return;
    const jsonStr = JSON.stringify(config, null, 2);
    const url = window.URL.createObjectURL(new Blob([jsonStr], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = this.downloadFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  showNewDialog(): void {
    this.newFileName.set('');
    this.newDialogVisible.set(true);
  }

  submitNewFile(): void {
    const name = this.newFileName().trim();
    if (!name) return;
    this.newDialogVisible.set(false);
    this.createFile.emit(name);
  }

  confirmDelete(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to delete this config file? This will switch to the next available file.',
      header: 'Delete Config',
      icon: 'pi pi-trash',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteFile.emit()
    });
  }
}
